import { m } from "framer-motion";
import { useTranslate } from "src/locales";
import { useAuthContext } from "src/auth/hooks";
import { useMemo, useState, useEffect, useCallback } from "react";
import {
  getNewsFeed,
  getUnreadReleaseCount,
  markProductReleasesAsSeen,
  getProductReleasesLastSeenAt,
} from "src/data/product-releases";

import { List , Stack , Badge , Drawer , Button , Divider , Tooltip , IconButton , Typography } from '@mui/material';

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";
import { useResponsive } from "src/hooks/use-responsive";

import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar";
import { varHover } from "src/components/animate";

import ReleasesPopoverItem from "./releases-popover-item";

// ----------------------------------------------------------------------

export default function ReleasesPopover() {
  const { t } = useTranslate();
  const { user } = useAuthContext();
  const router = useRouter();
  const drawer = useBoolean();
  const smUp = useResponsive("up", "sm");

  const releases = useMemo(() => getNewsFeed(user), [user]);

  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnreadCount = useCallback(() => {
    setUnreadCount(getUnreadReleaseCount(getProductReleasesLastSeenAt(), user));
  }, [user]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  const handleOpenDrawer = useCallback(() => {
    drawer.onTrue();
    markProductReleasesAsSeen(user);
    setUnreadCount(0);
  }, [drawer, user]);

  const handleNavigateToRelease = useCallback(
    (item) => {
      drawer.onFalse();
      router.push(
        item.kind === "guide"
          ? paths.dashboard.news.guide(item.slug)
          : paths.dashboard.news.details(item.slug),
      );
    },
    [drawer, router],
  );

  const handleViewAll = useCallback(() => {
    drawer.onFalse();
    router.push(paths.dashboard.news.root);
  }, [drawer, router]);

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 72 }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          color: "primary.main",
          bgcolor: "primary.lighter",
        }}
      >
        <Iconify icon="solar:rocket-bold-duotone" width={24} />
      </Stack>

      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h6">
          {t("product_updates.popover_title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("product_updates.label")}
        </Typography>
      </Stack>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      {releases.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
          {t("product_updates.empty")}
        </Typography>
      ) : (
        <List disablePadding>
          {releases.map((release) => (
            <ReleasesPopoverItem
              key={`${release.kind}-${release.slug}`}
              release={release}
              onClick={() => handleNavigateToRelease(release)}
            />
          ))}
        </List>
      )}
    </Scrollbar>
  );

  if (!releases.length) {
    return null;
  }

  return (
    <>
      <Tooltip title={t("product_updates.popover_title")}>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          color={drawer.value ? "primary" : "default"}
          onClick={handleOpenDrawer}
          aria-label={t("product_updates.popover_title")}
          sx={{
            width: 40,
            height: 40,
            ...(drawer.value && {
              bgcolor: "primary.lighter",
              "&:hover": {
                bgcolor: "primary.lighter",
              },
            }),
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <Iconify icon="solar:rocket-bold-duotone" width={24} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        {renderList}

        <Stack sx={{ p: 2.5 }}>
          <Button
            fullWidth
            size="large"
            variant="soft"
            endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
            onClick={handleViewAll}
          >
            {t("product_updates.see_all")}
          </Button>
        </Stack>
      </Drawer>
    </>
  );
}
